import clsx from 'clsx'
import { useEffect, useMemo } from 'react'
import { KpiCard, KpiStrip } from '../../../cc'
import { ReportTable, ReportTablePagination } from '../../../cc/ReportTable'
import { reportPill, reportSegment, reportSegmentBtn } from '../../ui'
import { LIST_PAGE_SIZE, toApiParams } from '../alertFilters'
import { fmtInt, pct } from '../alertLabels'
import type { AlertListEnvelope, AlertListRow, AlertListSort } from '../alertTypes'
import { useAlertAnalytics } from '../useAlertAnalytics'
import { asRows, ClickHint, columnDefs, makeExport, useViewExport, ViewCard, ViewState, type ViewProps } from './viewKit'

const COLUMNS = [
  'ts', 'severity', 'kind', 'vehicle_id', 'driver_label', 'place', 'zone', 'message', 'trip_id', 'acknowledged_by',
] as const
const EXPORT_COLUMNS = [
  'ts', 'severity', 'kind', 'vehicle_id', 'driver_label', 'place', 'zone', 'message', 'occurrence_count',
  'trip_id', 'trip_started_ts', 'trip_ended_ts', 'origin_hub', 'destination_hub', 'load_state',
  'acknowledged_at', 'acknowledged_by', 'lat', 'lon', 'alert_id',
] as const

const SORTS: { id: AlertListSort; label: string }[] = [
  { id: 'ts_desc', label: 'Newest first' },
  { id: 'ts_asc', label: 'Oldest first' },
  { id: 'severity', label: 'Severity' },
]

/** Every deduped alert in the window, one page of 100 at a time, server-paged and
 *  server-sorted (the window can hold tens of thousands). A row click selects the alert
 *  -- the URL carries `alert=<id>` and the shell opens the trip drawer on it. */
export function AlertListView({ filters, setFilters }: ViewProps): JSX.Element {
  const params = useMemo(() => {
    const p = toApiParams(filters)
    p.set('limit', String(LIST_PAGE_SIZE))
    p.set('offset', String(filters.page * LIST_PAGE_SIZE))
    p.set('sort', filters.sort)
    return p
  }, [filters])
  const { rows, envelope, status, error } = useAlertAnalytics<AlertListRow, AlertListEnvelope>('/list', params)

  const total = envelope?.total ?? rows[0]?.total_count ?? rows.length
  const offset = filters.page * LIST_PAGE_SIZE
  const totalPages = Math.max(1, Math.ceil(total / LIST_PAGE_SIZE))

  const pageStats = useMemo(() => ({
    critical: rows.filter((r) => r.severity === 'CRITICAL').length,
    acked: rows.filter((r) => r.acknowledged_at !== null).length,
    withTrip: rows.filter((r) => r.trip_id !== null).length,
  }), [rows])

  const selectedIdx = useMemo(() => {
    if (!filters.alert) return new Set<number>()
    const i = rows.findIndex((r) => r.alert_id === filters.alert)
    return i >= 0 ? new Set([offset + i]) : new Set<number>()
  }, [rows, filters.alert, offset])

  const defs = useMemo(() => columnDefs(COLUMNS, {
    message: {
      render: (v) => <span className="block max-w-[28rem] truncate text-[12px] text-slate-600" title={typeof v === 'string' ? v : undefined}>{typeof v === 'string' && v ? v : '—'}</span>,
    },
    trip_id: {
      render: (v, row) => (v
        ? <span className={reportPill('info')} title={String(v)}>{String(row.origin_hub ?? '?')} → {String(row.destination_hub ?? '?')}</span>
        : <span className={reportPill('neutral')}>no trip</span>),
    },
    acknowledged_by: {
      render: (v) => (v ? <span className="text-[12px] text-slate-600">{String(v)}</span> : <span className="text-[11px] text-slate-400">open</span>),
    },
  }), [])

  const exportFn = useMemo(
    () => makeExport(rows, [...EXPORT_COLUMNS], `alerts-list-p${filters.page + 1}`, 'Alert list', filters, ['ts', 'trip_started_ts', 'trip_ended_ts', 'acknowledged_at']),
    [rows, filters],
  )
  useViewExport(exportFn)

  // A page past the end (filters tightened while on page 4) is not "no data" -- snap back.
  const pastEnd = status === 'ok' && rows.length === 0 && total > 0 && filters.page > 0
  useEffect(() => { if (pastEnd) setFilters({ page: 0 }) }, [pastEnd, setFilters])

  return (
    <ViewState
      status={status} error={error} rowCount={pastEnd ? 1 : rows.length} filters={filters} setFilters={setFilters}
      noData={{ title: 'No alerts in this window', body: 'Nothing fired for this site between these dates.' }}
    >
      <div className="space-y-4">
        <KpiStrip>
          <KpiCard label="Alerts in window" value={total} />
          <KpiCard label="On this page" value={rows.length} sub={`page ${filters.page + 1} of ${fmtInt(totalPages)}`} />
          <KpiCard label="Critical (page)" value={pageStats.critical} tone={pageStats.critical > 0 ? 'danger' : 'neutral'} dot={pageStats.critical > 0} />
          <KpiCard label="Acknowledged (page)" value={pct(pageStats.acked, rows.length)} unit="%" sub={pageStats.acked === 0 ? 'nobody has worked these' : `${fmtInt(pageStats.acked)} of ${fmtInt(rows.length)}`} />
          <KpiCard label="Inside a trip (page)" value={pct(pageStats.withTrip, rows.length)} unit="%" sub={pageStats.withTrip < rows.length ? `${fmtInt(rows.length - pageStats.withTrip)} with no containing trip` : 'every alert has a trip'} />
        </KpiStrip>

        <ViewCard
          title="Alerts"
          sub="Deduped: repeated OVERSPEED/GHAT_OVERSPEED/DIVERSION rows are one episode. Click an alert to open the trip it happened in."
          actions={(
            <div className={reportSegment} role="group" aria-label="Sort order">
              {SORTS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  aria-pressed={filters.sort === s.id}
                  onClick={() => setFilters({ sort: s.id })}
                  className={clsx(reportSegmentBtn(filters.sort === s.id), '!px-2.5 !py-1 !text-[11.5px]')}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
          bodyClassName="p-0"
        >
          <ClickHint>Sorted on the server; column headers are not sortable here.</ClickHint>
          <ReportTable
            columns={[...COLUMNS]}
            rows={asRows(rows)}
            columnDefs={defs}
            selected={selectedIdx}
            rowOffset={offset}
            onRowClick={(row) => setFilters({ alert: String(row.alert_id) })}
            compact
            applyLoadStateLabels
            className="rounded-none ring-0 shadow-none"
          />
          <div className="border-t border-slate-200 p-3">
            <ReportTablePagination
              page={filters.page}
              totalPages={totalPages}
              pageSize={LIST_PAGE_SIZE}
              pageSizes={[LIST_PAGE_SIZE]}
              showingFrom={rows.length ? offset + 1 : 0}
              showingTo={offset + rows.length}
              totalCount={total}
              onPageChange={(p) => setFilters({ page: Math.max(0, Math.min(totalPages - 1, p)) })}
              onPageSizeChange={() => undefined}
            />
          </div>
        </ViewCard>
      </div>
    </ViewState>
  )
}
